/* eslint-disable camelcase */
import React, { useEffect, useState, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQuery, api } from "../../../api"

import PrintIcon from "@mui/icons-material/Print"
import CloseIcon from "@mui/icons-material/Close"
import { useReactToPrint } from "react-to-print"
import Swal from "sweetalert2"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(2),
  flexGrow: 1,
  padding: "0 16px",
  minHeight: "80vh",
}))

// =======================================================================
// COMPONENTE DE REPORTE PARA IMPRESIÓN (Datos desde SP sp_cxc_imprime_cobro)
// =======================================================================
const ReporteComprobanteCobro = ({ reporteData, ciaNombre }) => {
  // SEGURO DE VIDA: Si reporteData no es un arreglo válido, se aborta el renderizado
  if (!reporteData || !Array.isArray(reporteData) || reporteData.length === 0) return null

  // Extracción y agrupación de los resultados del SP usando grupo y subgrupo
  const cabecera = reporteData[0] || {}
  const formasPago = reporteData.filter((r) => r.grupo === "A" && r.subgrupo === "A" && r.cxctippag !== "")
  const facturas = reporteData.filter((r) => r.grupo === "B" && r.subgrupo === "A" && r.facnumfac !== "")
  const garantiasGeneradas = reporteData.filter((r) => r.grupo === "A" && r.subgrupo === "B" && r.cxctippag !== "")
  const facturasGarantias = reporteData.filter((r) => r.grupo === "B" && r.subgrupo === "B" && r.facnumfac !== "")

  const now = new Date()
  const fecImpresion = now.toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" })
  const horaImpresion = now.toLocaleTimeString("es-EC", { hour12: false })

  // Función para mapear el Estado de la Factura
  const mapEstadoFactura = (status) => {
    switch (status) {
      case "P":
        return "PENDIENTE"
      case "C":
        return "CANCELADA"
      case "A":
        return "ANULADA"
      default:
        return `${status || ""} - NO DEFINIDO`
    }
  }

  return (
    <div style={{ padding: "30px", fontFamily: "Arial, sans-serif", color: "#000", backgroundColor: "#fff" }}>
      {/* ENCABEZADO RESTRINGIDO A 10 CM MÁXIMO */}
      <div
        style={{
          maxWidth: "10cm",
          margin: "0 auto",
          textAlign: "center",
          borderBottom: "1px solid #000",
          paddingBottom: "5px",
          marginBottom: "10px",
          lineHeight: "1.1",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "14px", fontWeight: "bold", textTransform: "uppercase" }}>{ciaNombre}</h2>
        <h3 style={{ margin: "2px 0", fontSize: "12px" }}>COMPROBANTE DE COBRO</h3>
        <h4 style={{ margin: 0, fontSize: "12px", fontWeight: "bold" }}>No. {cabecera.cxcnumcomp}</h4>
      </div>

      {/* DATOS GENERALES (Compactos) */}
      <table style={{ width: "100%", fontSize: "10px", marginBottom: "10px", lineHeight: "1.2" }}>
        <tbody>
          <tr>
            <td style={{ width: "15%", fontWeight: "bold", padding: "2px 0" }}>Cliente</td>
            <td style={{ width: "45%", padding: "2px 0" }}>
              | {cabecera.clicodigo} - {cabecera.clinombre}
            </td>
            <td style={{ width: "15%", fontWeight: "bold", padding: "2px 0" }}>Fecha Emisión</td>
            <td style={{ width: "25%", padding: "2px 0" }}>| {cabecera.cxcfecemi}</td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", padding: "2px 0" }}>R.U.C.</td>
            <td style={{ padding: "2px 0" }}>| {cabecera.cliruc}</td>
            <td style={{ fontWeight: "bold", padding: "2px 0" }}>Estado</td>
            <td style={{ padding: "2px 0" }}>
              | {cabecera.cxcstatus === "C" ? "PROCESADO" : cabecera.cxcstatus === "A" ? "ANULADO" : cabecera.cxcstatus}
            </td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", padding: "2px 0" }}>Recaudador</td>
            <td style={{ padding: "2px 0" }}>
              | {cabecera.reccodigo} - {cabecera.recnombre}
            </td>
            <td style={{ fontWeight: "bold", padding: "2px 0" }}>Origen</td>
            <td style={{ padding: "2px 0" }}>| {cabecera.cxcdetalle}</td>
          </tr>
          <tr>
            <td style={{ fontWeight: "bold", padding: "2px 0" }}>Caja</td>
            <td colSpan="3" style={{ padding: "2px 0" }}>
              | {cabecera.cjacodigo} - {cabecera.cjadescri}
            </td>
          </tr>
        </tbody>
      </table>

      {/* 1. FORMAS DE PAGO (Grupo A / Subgrupo A) */}
      {formasPago && formasPago.length > 0 && (
        <div style={{ marginBottom: "15px" }}>
          <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "3px", borderBottom: "1px solid #000" }}>
            Formas de Pago / Recaudo
          </div>
          <table style={{ width: "100%", fontSize: "10px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #ccc" }}>
                <th style={{ textAlign: "left", padding: "2px" }}>FORMA DE COBRO</th>
                <th style={{ textAlign: "left", padding: "2px" }}>BANCO</th>
                <th style={{ textAlign: "left", padding: "2px" }}>No CTA.TAR</th>
                <th style={{ textAlign: "left", padding: "2px" }}>CHEQUE</th>
                <th style={{ textAlign: "left", padding: "2px" }}>REFERENCIA</th>
                <th style={{ textAlign: "right", padding: "2px" }}>VALOR</th>
              </tr>
            </thead>
            <tbody>
              {formasPago.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px dashed #eee" }}>
                  <td style={{ padding: "2px" }}>{item.descricobro}</td>
                  <td style={{ padding: "2px" }}>{item.bcodescri || "N/A"}</td>
                  <td style={{ padding: "2px" }}>{item.cxcnumcta || "N/A"}</td>
                  <td style={{ padding: "2px" }}>{item.cxcnumche || "N/A"}</td>
                  <td style={{ padding: "2px" }}>{item.cxccodrelg || "N/A"}</td>
                  <td style={{ textAlign: "right", padding: "2px", fontWeight: "bold" }}>
                    ${Number(item.cxcvalreal || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 2. FACTURAS APLICADAS (Grupo B / Subgrupo A) */}
      {facturas && facturas.length > 0 && (
        <div style={{ marginBottom: "15px" }}>
          <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "3px", borderBottom: "1px solid #000" }}>
            Facturas Aplicadas
          </div>
          <table style={{ width: "100%", fontSize: "10px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #ccc" }}>
                <th style={{ textAlign: "left", padding: "2px" }}>FACTURA / ND</th>
                <th style={{ textAlign: "center", padding: "2px" }}>CUOTA</th>
                <th style={{ textAlign: "left", padding: "2px" }}>EMISIÓN</th>
                <th style={{ textAlign: "left", padding: "2px" }}>VENCIMIENTO</th>
                <th style={{ textAlign: "left", padding: "2px" }}>ESTADO</th>
                <th style={{ textAlign: "right", padding: "2px" }}>VALOR</th>
                <th style={{ textAlign: "right", padding: "2px" }}>SALDO</th>
              </tr>
            </thead>
            <tbody>
              {facturas.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px dashed #eee" }}>
                  <td style={{ padding: "2px" }}>
                    {item.serie ? `${item.serie} - ${item.facnumfac}` : item.facnumfac}
                  </td>
                  <td style={{ textAlign: "center", padding: "2px" }}>
                    {item.facsecuen} de {item.cuotas || item.facsecuen}
                  </td>
                  <td style={{ padding: "2px" }}>{item.facfecemi ? item.facfecemi.split(" ")[0] : "N/A"}</td>
                  <td style={{ padding: "2px" }}>{item.facfecven ? item.facfecven.split(" ")[0] : "N/A"}</td>
                  <td style={{ padding: "2px" }}>{mapEstadoFactura(item.facstatus)}</td>
                  <td style={{ textAlign: "right", padding: "2px", fontWeight: "bold" }}>
                    ${Number(item.cxcabono).toFixed(2)}
                  </td>
                  <td style={{ textAlign: "right", padding: "2px" }}>${Number(item.facsaldo).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 3. DOCUMENTOS EN GARANTÍA GENERADOS (Grupo A / Subgrupo B) */}
      {garantiasGeneradas && garantiasGeneradas.length > 0 && (
        <div style={{ marginBottom: "15px" }}>
          <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "3px", borderBottom: "1px solid #000" }}>
            Documentos en Garantía Generados
          </div>
          <table style={{ width: "100%", fontSize: "10px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #ccc" }}>
                <th style={{ textAlign: "left", padding: "2px" }}>TIPO DOC</th>
                <th style={{ textAlign: "left", padding: "2px" }}>BANCO</th>
                <th style={{ textAlign: "left", padding: "2px" }}>CTA / REFERENCIA</th>
                <th style={{ textAlign: "left", padding: "2px" }}>NUM CHEQUE</th>
                <th style={{ textAlign: "right", padding: "2px" }}>VALOR</th>
              </tr>
            </thead>
            <tbody>
              {garantiasGeneradas.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px dashed #eee" }}>
                  <td style={{ padding: "2px" }}>{item.cxctippag}</td>
                  <td style={{ padding: "2px" }}>{item.bcodescri || "N/A"}</td>
                  <td style={{ padding: "2px" }}>{item.cxcnumcta || "N/A"}</td>
                  <td style={{ padding: "2px" }}>{item.cxcnumche || "N/A"}</td>
                  <td style={{ textAlign: "right", padding: "2px", fontWeight: "bold" }}>
                    ${Number(item.cxcvalreal || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 4. FACTURAS ASOCIADAS A GARANTÍAS (Grupo B / Subgrupo B) */}
      {facturasGarantias && facturasGarantias.length > 0 && (
        <div style={{ marginBottom: "15px" }}>
          <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "3px", borderBottom: "1px solid #000" }}>
            Facturas Asociadas a Garantías
          </div>
          <table style={{ width: "100%", fontSize: "10px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #ccc" }}>
                <th style={{ textAlign: "left", padding: "2px" }}>FACTURA / ND</th>
                <th style={{ textAlign: "center", padding: "2px" }}>CUOTA</th>
                <th style={{ textAlign: "left", padding: "2px" }}>EMISIÓN</th>
                <th style={{ textAlign: "left", padding: "2px" }}>VENCIMIENTO</th>
                <th style={{ textAlign: "left", padding: "2px" }}>ESTADO</th>
                <th style={{ textAlign: "right", padding: "2px" }}>VALOR</th>
                <th style={{ textAlign: "right", padding: "2px" }}>SALDO</th>
              </tr>
            </thead>
            <tbody>
              {facturasGarantias.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px dashed #eee" }}>
                  <td style={{ padding: "2px" }}>
                    {item.serie ? `${item.serie} - ${item.facnumfac}` : item.facnumfac}
                  </td>
                  <td style={{ textAlign: "center", padding: "2px" }}>
                    {item.facsecuen} de {item.cuotas || item.facsecuen}
                  </td>
                  <td style={{ padding: "2px" }}>{item.facfecemi ? item.facfecemi.split(" ")[0] : "N/A"}</td>
                  <td style={{ padding: "2px" }}>{item.facfecven ? item.facfecven.split(" ")[0] : "N/A"}</td>
                  <td style={{ padding: "2px" }}>{mapEstadoFactura(item.facstatus)}</td>
                  <td style={{ textAlign: "right", padding: "2px", fontWeight: "bold" }}>
                    ${Number(item.cxcabono).toFixed(2)}
                  </td>
                  <td style={{ textAlign: "right", padding: "2px" }}>${Number(item.facsaldo).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TOTALES Y SON */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px", alignItems: "flex-end" }}>
        <div style={{ fontSize: "10px", width: "60%" }}>
          <b>SON:</b> {cabecera.valletras}
        </div>
        <table style={{ width: "35%", fontSize: "11px" }}>
          <tbody>
            <tr>
              <td style={{ padding: "2px 0", fontWeight: "bold" }}>VALOR COBRADO:</td>
              <td style={{ textAlign: "right", padding: "2px 0", fontWeight: "bold" }}>
                ${Number(cabecera.cxcmonto || 0).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* METADATA INFERIOR */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "9px",
          marginTop: "30px",
          borderTop: "1px solid #ddd",
          paddingTop: "5px",
        }}
      >
        <div>Doc. Interno - SIAC Web</div>
        <div>
          <span style={{ marginRight: "15px" }}>Impreso: {fecImpresion}</span>
          <span>Hora: {horaImpresion}</span>
        </div>
      </div>

      {/* FIRMAS */}
      <table style={{ width: "100%", fontSize: "10px", textAlign: "center", marginTop: "50px" }}>
        <tbody>
          <tr>
            <td style={{ width: "33%" }}>
              <div style={{ borderTop: "1px solid #000", margin: "0 20px", paddingTop: "4px" }}>ENTREGADO POR</div>
            </td>
            <td style={{ width: "33%" }}>
              <div style={{ borderTop: "1px solid #000", margin: "0 20px", paddingTop: "4px" }}>REVISADO POR</div>
            </td>
            <td style={{ width: "33%" }}>
              <div style={{ borderTop: "1px solid #000", margin: "0 20px", paddingTop: "4px" }}>RECIBÍ CONFORME</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const CobrosDetalleIndex = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const cxcnumcomp = location.state?.cxcnumcomp

  const [modalImpresionOpen, setModalImpresionOpen] = useState(false)
  const [reporteData, setReporteData] = useState(null)
  const [isPrinting, setIsPrinting] = useState(false)
  const componentRef = useRef()

  let ciaNombreActual = "EMPRESA S.A."
  try {
    const jwtDataStr = localStorage.getItem("jwt")
    if (jwtDataStr) {
      const jwtData = JSON.parse(jwtDataStr)
      ciaNombreActual = jwtData?.seleccion?.cliciacianombre || "EMPRESA S.A."
    }
  } catch (error) {
    console.error("Error al leer datos de la compañía:", error)
  }

  useEffect(() => {
    if (!cxcnumcomp) {
      navigate(-1)
    }
  }, [cxcnumcomp, navigate])

  const { data: detalleCobro, isLoading } = useQuery({
    queryKey: ["getDetalleCobro", cxcnumcomp],
    queryFn: async () => {
      try {
        const response = await api.post("/ConsultasCobrosAplA/getDetalleCobro", { cxcnumcomp })
        const resData = response?.data
        if (resData?.data?.data?.cabecera) return resData.data.data
        if (resData?.data?.cabecera) return resData.data
        if (resData?.cabecera) return resData
        return null
      } catch (error) {
        console.error("Error al obtener detalle del cobro:", error)
        return null
      }
    },
    enabled: !!cxcnumcomp,
    refetchOnWindowFocus: false,
  })

  // =======================================================================
  // FUNCIÓN PARA OBTENER LA DATA DEL SP Y ABRIR EL MODAL (CON BÚSQUEDA PROFUNDA)
  // =======================================================================
  const handleOpenPrintModal = async () => {
    try {
      setIsPrinting(true)
      const res = await api.post("/ConsultasCobrosAplA/getReporteCobro", { cxcnumcomp })

      let arrData = null
      if (Array.isArray(res)) arrData = res
      else if (Array.isArray(res?.data)) arrData = res.data
      else if (Array.isArray(res?.data?.data)) arrData = res.data.data
      else if (Array.isArray(res?.data?.data?.data)) arrData = res.data.data.data

      if (arrData && arrData.length > 0) {
        setReporteData(arrData)
        setModalImpresionOpen(true)
      } else {
        Swal.fire("Aviso", "No se encontró información para imprimir el comprobante.", "info")
      }
    } catch (e) {
      Swal.fire("Error", "Ocurrió un error al obtener el reporte.", "error")
    } finally {
      setIsPrinting(false)
    }
  }

  const handlePrintAction = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Comprobante_${cxcnumcomp}`,
  })

  if (!detalleCobro) return <CustomBackdrop isLoading={isLoading} />

  const { cabecera, facturas, postfechados_total, garantias, formas_pago } = detalleCobro
  const granTotal = Number(cabecera?.cxcmonto || 0) + Number(postfechados_total || 0)

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, px: 3 }}>
          <Typography variant="h5" fontWeight="bold" color="primary">
            Detalle del Cobro: {cxcnumcomp}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handleOpenPrintModal}
            disabled={isPrinting}
            sx={{ fontWeight: "bold" }}
          >
            {isPrinting ? "Generando..." : "Imprimir Comprobante"}
          </Button>
        </Box>

        <CustomBackdrop isLoading={isLoading || isPrinting} />

        <StyledRoot>
          {/* BLOQUE 1: DATOS DE CABECERA (Visualización en Pantalla) */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              Información General del Documento
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth disabled size="small" label="Comprobante" value={cabecera?.cxcnumcomp || ""} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth disabled size="small" label="Fecha Emisión" value={cabecera?.cxcfecemi || ""} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled
                  size="small"
                  label="Origen / Naturaleza"
                  value={cabecera?.cxcorigen || ""}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField fullWidth disabled size="small" label="Cód. Cliente" value={cabecera?.clicodigo || ""} />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  disabled
                  size="small"
                  label="Razón Social / Nombre"
                  value={cabecera?.clinombre || ""}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth disabled size="small" label="R.U.C." value={cabecera?.cliruc || ""} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  disabled
                  size="small"
                  label="Recaudador"
                  value={`${cabecera?.reccodigo || ""} - ${cabecera?.recnombre || ""}`}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth disabled size="small" label="Observaciones" value={cabecera?.cxcdetalle || ""} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 3, mt: 1, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography variant="body2">
                    <b>Estado:</b>{" "}
                    {cabecera?.cxcstatus === "C"
                      ? "PROCESADO"
                      : cabecera?.cxcstatus === "A"
                        ? "ANULADO"
                        : cabecera?.cxcstatus}
                  </Typography>
                  <Typography variant="body2">
                    <b>Usuario Sistema:</b> {cabecera?.cxcusrisys}
                  </Typography>
                  <Typography variant="body2">
                    <b>Fecha/Hora Sis:</b> {cabecera?.cxcfecisys} {cabecera?.cxchorisys}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* BLOQUE 2: DISTRIBUCIÓN DEL COBRO */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
              Aplicación y Distribución del Cobro
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {facturas && facturas.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#424242" }}>
                  Facturas Aplicadas (Cobro Normal)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#e0f7fa" }}>
                      <TableRow>
                        <TableCell>
                          <b>Factura</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>Cuota</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Saldo Anterior</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Valor Aplicado (Abono)</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>N/D Factura</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {facturas.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.facnumfac}</TableCell>
                          <TableCell align="center">{row.facsecuen}</TableCell>
                          <TableCell align="right">${Number(row.facsaldo).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "primary.main" }}>
                            ${Number(row.cxcabono).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">{row.cxcndfac || "N/A"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {garantias && garantias.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#2E7D32" }}>
                  Documentos en Garantía Aplicados
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ border: "1px solid #81c784" }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#e8f5e9" }}>
                      <TableRow>
                        <TableCell>
                          <b>Factura</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>Cuota</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Saldo Garantizado</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Valor Aplicado</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {garantias.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.facnumfac}</TableCell>
                          <TableCell align="center">{row.facsecuen}</TableCell>
                          <TableCell align="right">${Number(row.facsaldo).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "#2E7D32" }}>
                            ${Number(row.cxcabono).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* BLOQUE 3: FORMAS DE PAGO */}
            {formas_pago && formas_pago.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#1565c0" }}>
                  Formas de Pago / Recaudo
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ border: "1px solid #90caf9" }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#e3f2fd" }}>
                      <TableRow>
                        <TableCell>
                          <b>Tipo de Pago</b>
                        </TableCell>
                        <TableCell>
                          <b>Banco</b>
                        </TableCell>
                        <TableCell>
                          <b>Cuenta / Tarjeta</b>
                        </TableCell>
                        <TableCell>
                          <b>No. Referencia / Cheque</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Valor Ingresado</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formas_pago.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.cxctippag}</TableCell>
                          <TableCell>{row.bcodescri || row.bcocodigo || "N/A"}</TableCell>
                          <TableCell>{row.cxcctacte || "N/A"}</TableCell>
                          <TableCell>{row.cxcnumche || "N/A"}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold", color: "#1565c0" }}>
                            ${Number(row.cxcmonto || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* RESUMEN DE TOTALES */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#f5f5f5", minWidth: "300px", border: "1px solid #e0e0e0" }}>
                <Grid container spacing={1}>
                  {postfechados_total > 0 && (
                    <>
                      <Grid item xs={7}>
                        <Typography variant="body2">Chq. Postfechados:</Typography>
                      </Grid>
                      <Grid item xs={5} align="right">
                        <Typography variant="body2">${Number(postfechados_total).toFixed(2)}</Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={7}>
                    <Typography variant="body2" fontWeight="bold">
                      Valor Cobrado:
                    </Typography>
                  </Grid>
                  <Grid item xs={5} align="right">
                    <Typography variant="body2" fontWeight="bold">
                      ${Number(cabecera?.cxcmonto || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="subtitle1" color="error" fontWeight="bold">
                      TOTAL GENERAL:
                    </Typography>
                  </Grid>
                  <Grid item xs={5} align="right">
                    <Typography variant="subtitle1" color="error" fontWeight="bold">
                      ${granTotal.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Paper>
        </StyledRoot>

        {/* MODAL DE VISTA PREVIA E IMPRESIÓN */}
        <Dialog
          open={modalImpresionOpen}
          onClose={() => setModalImpresionOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { minHeight: "80vh", backgroundColor: "#525659" } }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "white" }}
          >
            <Typography variant="h6" fontWeight="bold">
              Vista Previa de Impresión
            </Typography>
            <IconButton onClick={() => setModalImpresionOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Box
              sx={{
                width: "210mm",
                minHeight: "297mm",
                backgroundColor: "white",
                boxShadow: "0px 0px 10px rgba(0,0,0,0.5)",
                overflow: "hidden",
              }}
            >
              {/* Componente Referenciado para Impresión (Mapeo SP) */}
              <div ref={componentRef}>
                <ReporteComprobanteCobro reporteData={reporteData} ciaNombre={ciaNombreActual} />
              </div>
            </Box>
          </DialogContent>

          <DialogActions sx={{ backgroundColor: "white", p: 2 }}>
            <Button onClick={() => setModalImpresionOpen(false)} color="inherit" sx={{ fontWeight: "bold" }}>
              Cancelar
            </Button>
            <Button
              onClick={handlePrintAction}
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              sx={{ px: 4, fontWeight: "bold" }}
            >
              Confirmar Impresión
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default CobrosDetalleIndex
